import { motion } from 'framer-motion'
import { Link, useParams } from 'react-router-dom'
import {
  getSectorByLocaleSlug,
  SECTOR_BASE,
  SECTOR_CONTENT,
  type SectorId,
} from '../data/sectors'
import SectorFlowAnimation from '../components/sector/SectorFlowAnimation'
import SectorSeasonalAnimation from '../components/sector/SectorSeasonalAnimation'
import { orgSchema, breadcrumbSchema } from '../lib/seo'
import SeoHead from '../components/SeoHead'
import { useT } from '../i18n/LocaleContext'
import { localizedPath, dynamicAlternates } from '../i18n/routes'

// Realistic seasonal demand curves per sector (12 months Jan→Dec, 0..1 normalized)
const CURVES: Record<SectorId, number[]> = {
  mode: [0.45, 0.4, 0.5, 0.55, 0.5, 0.45, 0.6, 0.65, 0.85, 0.9, 0.95, 1.0],
  cosmetique: [0.45, 0.5, 0.55, 0.5, 0.7, 0.55, 0.5, 0.5, 0.55, 0.65, 0.95, 1.0],
  electronique: [0.4, 0.4, 0.45, 0.5, 0.5, 0.55, 0.55, 0.6, 0.65, 0.7, 0.95, 1.0],
  'jardin-exterieur': [0.25, 0.35, 0.7, 0.95, 1.0, 0.85, 0.65, 0.55, 0.45, 0.35, 0.3, 0.3],
  'maison-decoration': [0.4, 0.4, 0.45, 0.5, 0.55, 0.55, 0.5, 0.5, 0.6, 0.7, 0.95, 1.0],
  parfumerie: [0.4, 0.45, 0.5, 0.5, 0.55, 0.7, 0.5, 0.5, 0.55, 0.6, 0.95, 1.0],
}

export default function SectorPage() {
  const { slug } = useParams()
  const { t, locale } = useT()
  const found = slug ? getSectorByLocaleSlug(locale, slug) : undefined

  if (!found) {
    return (
      <main className="relative pt-32 pb-24 max-w-3xl mx-auto px-6 text-center">
        <h1 className="text-3xl font-semibold text-ink-950">{t('sectorPage.notFoundTitle')}</h1>
        <Link
          to={localizedPath('sectors', locale)}
          className="mt-6 inline-block text-accent-700 hover:text-accent-800"
        >
          {t('sectorPage.notFoundLink')}
        </Link>
      </main>
    )
  }

  const { base, content } = found
  const curve = CURVES[base.id] ?? CURVES.mode
  const inSector = content.inSector
  const detailPath = localizedPath('sectors.detail', locale, { slug: base.slug[locale] })
  const detailUrl = `https://yaslan.be${detailPath}`

  return (
    <>
    <SeoHead
      title={content.metaTitle}
      description={content.metaDescription}
      path={detailPath}
      alternates={dynamicAlternates('sectors.detail', base.slug)}
      jsonLd={[
        {
          '@type': 'Service',
          '@id': `${detailUrl}#service`,
          name: content.h1,
          serviceType: `Logistique e-commerce ${content.inSector}`,
          description: content.metaDescription,
          provider: { '@id': 'https://yaslan.be/#organization' },
          areaServed: ['Europe', 'United Kingdom', 'Switzerland'],
          url: detailUrl,
        },
        {
          '@type': 'FAQPage',
          '@id': `${detailUrl}#faq`,
          inLanguage: locale,
          mainEntity: content.faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        orgSchema(locale),
        breadcrumbSchema([
          { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
          { name: t('breadcrumb.sectors'), path: localizedPath('sectors', locale) },
          { name: content.name, path: detailPath },
        ]),
      ]}
    />
    <main className="relative pt-28 sm:pt-32 pb-24 overflow-hidden isolate">
      {/* Background DA */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[1100px] -z-10 pointer-events-none">
        <div
          className="absolute inset-x-0 -top-20 h-[600px]"
          style={{
            background:
              'radial-gradient(ellipse at top, rgba(59,93,255,0.22), rgba(59,93,255,0.06) 45%, transparent 75%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 75%)',
          }}
        />
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -right-32 top-[20%] w-[420px] h-[420px] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)' }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-6">
        {/* ───────────── HERO ───────────── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xs sm:text-sm text-ink-500"
        >
          <Link to={localizedPath('home', locale)} className="hover:text-accent-700 transition-colors">
            {t('sectorPage.breadcrumbHome')}
          </Link>
          <span className="mx-2 text-ink-300">/</span>
          <Link to={localizedPath('sectors', locale)} className="hover:text-accent-700 transition-colors">
            {t('sectorPage.breadcrumbSectors')}
          </Link>
          <span className="mx-2 text-ink-300">/</span>
          <span className="text-ink-700">{content.name}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{content.badge}{t('sectorPage.badgeSuffix')}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="mt-5 sm:mt-6 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.05] max-w-4xl"
        >
          {content.taglineLead}{' '}
          <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
            {content.taglineItalic}
          </span>
          .
        </motion.h1>

        {/* SEO H1 visible as supporting line */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-4 text-sm text-accent-700 font-medium"
        >
          {content.h1}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-600 leading-relaxed max-w-3xl"
        >
          {content.heroIntro}
        </motion.p>

        {/* ───────────── 1. CHALLENGES ↔ RESPONSES ───────────── */}
        <section className="relative mt-20 sm:mt-28">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
          >
            {t('sectorPage.challengesHeadingLead')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {inSector}
            </span>
            .
          </motion.h2>

          <div className="mt-10 sm:mt-12 grid lg:grid-cols-2 gap-6 lg:gap-10">
            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-ink-500">
                {t('sectorPage.challengesConstat')}
              </p>
              <div className="mt-4 space-y-3">
                {content.challenges.map((c, i) => (
                  <motion.div
                    key={c.title}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="relative rounded-2xl border border-ink-100 bg-ink-100 p-5"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-rose-50 text-rose-600 border border-rose-100 flex items-center justify-center shrink-0">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-ink-950">{c.title}</p>
                        <p className="mt-1 text-sm text-ink-600 leading-relaxed">{c.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-accent-700">
                {t('sectorPage.challengesResponse')}
              </p>
              <div className="mt-4 space-y-3">
                {content.responses.map((r, i) => (
                  <motion.div
                    key={r.title}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.6, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="relative rounded-2xl border border-accent-100 bg-gradient-to-br from-white via-accent-50/40 to-white p-5"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-accent-600 to-accent-700 text-white flex items-center justify-center shadow-[0_8px_18px_-8px_rgba(59,93,255,0.7)] shrink-0">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
                          <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-ink-950">{r.title}</p>
                        <p className="mt-1 text-sm text-ink-600 leading-relaxed">{r.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ───────────── 2. ANIMATION 1 — FLOW ───────────── */}
        <section className="relative mt-20 sm:mt-28">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
          >
            {t('sectorPage.flowHeadingLead')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {inSector}
            </span>
            .
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-4 text-ink-600 max-w-2xl leading-relaxed"
          >
            {t('sectorPage.flowIntro')}
          </motion.p>

          <div className="mt-10">
            <SectorFlowAnimation
              steps={content.flowItems}
              badge={content.flowBadge}
              kicker={content.flowKicker}
            />
          </div>
        </section>

        {/* ───────────── 3. PROMISE BAND ───────────── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-20 sm:mt-28 max-w-5xl mx-auto text-center"
        >
          <motion.div
            aria-hidden
            animate={{ scale: [1, 1.12, 1], opacity: [0.3, 0.55, 0.3] }}
            transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none -z-10"
            style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)' }}
          />
          <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-accent-700">
            {t('sectorPage.promiseKicker')}
          </span>
          <blockquote className="mt-4 sm:mt-5 font-semibold tracking-[-0.025em] text-ink-950 text-[clamp(1.6rem,3.6vw,2.6rem)] leading-[1.15] max-w-3xl mx-auto">
            «&nbsp;{content.promise}&nbsp;»
          </blockquote>
        </motion.section>

        {/* ───────────── 4. ENGAGEMENTS CHIFFRÉS (replaces whyPoints) ───────────── */}
        <section className="relative mt-20 sm:mt-28">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{t('sectorPage.statsBadge')}</span>
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="mt-5 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
          >
            {t('sectorPage.statsHeadingLead')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {inSector}
            </span>
            &nbsp;?
          </motion.h2>

          <div className="mt-10 sm:mt-12 grid sm:grid-cols-3 gap-4 sm:gap-5">
            {content.stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group relative rounded-2xl sm:rounded-3xl border border-ink-100 bg-ink-100 p-6 sm:p-8 hover:border-accent-200 hover:shadow-[0_24px_60px_-22px_rgba(59,93,255,0.4)] transition-all overflow-hidden"
              >
                <motion.span
                  aria-hidden
                  className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl pointer-events-none opacity-0 group-hover:opacity-60 transition-opacity"
                  style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.35), transparent 70%)' }}
                />
                <div className="relative">
                  <div className="font-semibold tracking-[-0.04em] leading-none text-[clamp(2.2rem,5vw,3.4rem)] bg-gradient-to-br from-ink-950 via-accent-700 to-accent-500 bg-clip-text text-transparent">
                    {s.value}
                  </div>
                  <p className="mt-3 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-ink-500">
                    {s.label}
                  </p>
                  <p className="mt-2 text-sm sm:text-base text-ink-600 leading-relaxed">
                    {s.caption}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ───────────── 5. ANIMATION 2 — SEASONAL ───────────── */}
        <section className="relative mt-20 sm:mt-28">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
          >
            {t('sectorPage.seasonalHeadingLead')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {t('sectorPage.seasonalHeadingHighlight')}
            </span>
            .
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-4 text-ink-600 max-w-2xl leading-relaxed"
          >
            {t('sectorPage.seasonalIntro')}
          </motion.p>

          <div className="mt-10">
            <SectorSeasonalAnimation
              curve={curve}
              peakStart={base.peakHighlightStart}
              peakEnd={base.peakHighlightEnd}
              peakLabel={content.peakLabel}
              peakMonths={content.peakMonths}
              multiplier={content.peakMultiplier}
              kpi1={content.peakKpi1}
              kpi2={content.peakKpi2}
            />
          </div>
        </section>

        {/* ───────────── 6. FAQ ───────────── */}
        <section className="relative mt-20 sm:mt-28">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
          >
            {t('sectorPage.faqHeadingLead')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {t('sectorPage.faqHeadingHighlight', { sector: inSector })}
            </span>
            .
          </motion.h2>

          <div className="mt-10 space-y-3">
            {content.faqs.map((f, i) => (
              <motion.details
                key={f.q}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="group rounded-2xl border border-ink-100 bg-ink-100 p-5 sm:p-6 hover:border-accent-200 transition-colors"
              >
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                  <h3 className="text-base sm:text-lg font-semibold text-ink-950 tracking-tight">
                    {f.q}
                  </h3>
                  <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full border border-accent-200 bg-accent-50 text-accent-700 flex items-center justify-center transition-transform group-open:rotate-45">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                    </svg>
                  </span>
                </summary>
                <p className="mt-3 text-sm sm:text-base text-ink-600 leading-relaxed">{f.a}</p>
              </motion.details>
            ))}
          </div>
        </section>

        {/* ───────────── 7. OTHER SECTORS — internal linking ───────────── */}
        <section className="relative mt-20 sm:mt-28">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-ink-500">
            {t('sectorPage.otherSectors')}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {SECTOR_BASE.filter((b) => b.id !== base.id).map((b) => (
              <Link
                key={b.id}
                to={localizedPath('sectors.detail', locale, { slug: b.slug[locale] })}
                className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-ink-100 px-4 py-2 text-sm text-ink-700 hover:text-accent-700 hover:border-accent-300 hover:bg-accent-50 transition-colors"
              >
                {SECTOR_CONTENT[locale][b.id].name}
                <span aria-hidden>→</span>
              </Link>
            ))}
          </div>
        </section>

        {/* ───────────── 8. FINAL CTA — About-style dark dramatic ───────────── */}
        <section className="relative mt-20 sm:mt-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 text-white px-6 sm:px-12 py-16 sm:py-24 overflow-hidden isolate"
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-30 -z-10"
              style={{
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
                maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
              }}
            />
            <motion.div
              aria-hidden
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -right-32 -top-32 w-[460px] h-[460px] rounded-full blur-3xl -z-10"
              style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.55), transparent 70%)' }}
            />
            <motion.div
              aria-hidden
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
              className="absolute -left-32 -bottom-32 w-[380px] h-[380px] rounded-full blur-3xl -z-10"
              style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.35), transparent 70%)' }}
            />
            <motion.div
              aria-hidden
              animate={{ rotate: 360 }}
              transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
              className="absolute left-1/2 top-1/2 w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/2 -z-10"
            >
              <span className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-accent-400/80 blur-[2px]" />
              <span className="absolute right-[12%] top-[18%] w-1.5 h-1.5 rounded-full bg-accent-300/70 blur-[2px]" />
            </motion.div>

            <div className="relative max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-accent-200">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-70 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
                </span>
                {t('sectorPage.ctaBadge', { sector: content.badge.toLowerCase() })}
              </span>

              <h3 className="mt-6 font-semibold tracking-[-0.035em] text-[clamp(2.4rem,6vw,4.4rem)] leading-[1.02]">
                {t('sectorPage.ctaTitleLead')}{' '}
                <span className="font-semibold bg-gradient-to-r from-accent-300 via-accent-200 to-white bg-clip-text text-transparent pb-[0.08em] inline-block">
                  {t('sectorPage.ctaTitleHighlight')}
                </span>
                <br className="hidden sm:block" />{t('sectorPage.ctaTitleTail')}
              </h3>

              <p className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-300 leading-relaxed max-w-xl">
                {t('sectorPage.ctaCopy', { sector: content.badge.toLowerCase() })}
              </p>

              <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link
                  to={`${localizedPath('contact', locale)}#rendez-vous`}
                  className="relative group inline-flex items-center gap-2.5 bg-ink-100 hover:bg-accent-50 text-ink-950 rounded-full pl-6 pr-5 py-3.5 sm:py-4 text-sm sm:text-base font-semibold shadow-[0_24px_60px_-18px_rgba(59,93,255,0.7)] transition-colors overflow-hidden"
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-accent-200/60 to-transparent pointer-events-none"
                  />
                  <span className="relative">{t('sectorPage.ctaPrimary')}</span>
                  <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-ink-950 text-white">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </Link>
                <Link
                  to={localizedPath('sectors', locale)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-5 py-3.5 sm:py-4 text-sm sm:text-base font-medium text-white/90 hover:bg-white/10 hover:border-white/30 transition-colors"
                >
                  {t('sectorPage.ctaSecondary')}
                </Link>
              </div>

              <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs sm:text-sm text-ink-400">
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {t('sectorPage.trust1')}
                </span>
                <span className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('sectorPage.trust2')}
                </span>
                <span className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('sectorPage.trust3')}
                </span>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </main>
    </>
  )
}
